import './MenuPanel.css'

/** תפריט מורחב — גודל מברשת, נצנצים, בטל, נקה, סגנון, ייצוא. במצב קל: רק בטל, נקה, גלריה, תמונה חדשה, הדבק, שמור. */
export function MenuPanel({
  expertMode,
  brushSize,
  setBrushSize,
  glitterIntensity,
  setGlitterIntensity,
  onUndo,
  undoDisabled,
  onClear,
  onOpenExportModal,
  onFilterPicture,
  onToggleVideoRecording,
  isVideoRecording,
  onNewImage,
  onPasteImage,
  onSaveToGallery,
  onTakeScreenshot,
  onPhotoToPainting,
  onResizePicture,
  onRemoveBackground,
  onOpenGallery,
  tool,
  setTool,
  onSelectStamp,
  stampFontFamily,
  onSelectStampFont,
  onShareDrawing,
  onShareApp,
  children,
  lang = 'he',
}) {
  const t = (en, he) => (lang === 'en' ? en : he)
  const SPECIAL_SYMBOLS = ['★', '❤', '✿', '☀', '☁', '⚡', '✈', '♪', '∞', '☘', '☺', '✧']
  const HEBREW_LETTERS = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ז', 'ח', 'ט', 'י', 'כ', 'ל', 'מ', 'נ', 'ס', 'ע', 'פ', 'צ', 'ק', 'ר', 'ש', 'ת']
  const ENGLISH_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')
  const NUMBERS = Array.from({ length: 10 }, (_, i) => String(i + 1))

  const handleSelectStampValue = (value) => {
    if (!value) return
    onSelectStamp?.(value)
    setTool?.('stamp')
  }

  const actionButtonsTop = [
    { key: 'undo', label: t('Undo', 'בטל'), onClick: onUndo, disabled: undoDisabled },
    { key: 'clear', label: t('Clear colors', 'נקה צבעים'), onClick: onClear },
  ]

  const actionButtonsMiddle = [
    onOpenGallery && { key: 'gallery', label: t('Gallery', 'גלריה'), onClick: onOpenGallery },
    onNewImage && { key: 'new-image', label: t('New image', 'תמונה חדשה'), onClick: onNewImage },
    onPasteImage && { key: 'paste-image', label: t('Paste image', 'הדבק תמונה'), onClick: onPasteImage, title: t('Paste image (Ctrl+V)', 'הדבק תמונה מהלוח (Ctrl+V)') },
    onPhotoToPainting && { key: 'photo-to-painting', label: t('Photo to painting', 'הפוך תמונה לציור'), onClick: onPhotoToPainting },
    onRemoveBackground && { key: 'remove-bg', label: t('Remove background', 'הסר רקע'), onClick: onRemoveBackground },
    onResizePicture && { key: 'resize-picture', label: t('Resize picture', 'שנה גודל תמונה'), onClick: onResizePicture },
    onFilterPicture && { key: 'filter-picture', label: t('Filter picture', 'פילטר לתמונה'), onClick: onFilterPicture },
    onSaveToGallery && { key: 'save-gallery', label: t('Save to gallery', 'שמור לגלריה'), onClick: onSaveToGallery },
    onTakeScreenshot && { key: 'screenshot', label: t('Screenshot', 'צילום מסך'), onClick: onTakeScreenshot },
  ].filter(Boolean)

  const actionButtonsBottom = [
    expertMode && onOpenExportModal && { key: 'export-modal', label: t('Export (filter + crop)', 'ייצוא (פילטר + חיתוך)'), onClick: onOpenExportModal, className: 'primary' },
    expertMode && onToggleVideoRecording && {
      key: 'video-recording',
      label: isVideoRecording ? t('⏹ Stop recording', '⏹ עצור הקלטת וידאו') : t('🎬 Record video', '🎬 הקלטת וידאו'),
      onClick: onToggleVideoRecording,
      className: isVideoRecording ? 'primary' : '',
    },
    expertMode && onShareDrawing && { key: 'share-drawing', label: t('Share drawing', 'שתף ציור'), onClick: onShareDrawing },
    expertMode && onShareApp && { key: 'share-app', label: t('Share app', 'שתף אפליקציה'), onClick: onShareApp },
  ].filter(Boolean)

  return (
    <div className="menu-panel">
      {expertMode && (
        <section className="menu-panel-card" aria-label={t('Painting controls', 'שליטת ציור')}>
          <h3 className="menu-panel-card-title">{t('Painting controls', 'שליטת ציור')}</h3>
          <div className="menu-panel-section">
            <span className="menu-panel-label">{t('Brush size', 'גודל מברשת')}</span>
            <input type="range" min="2" max="60" value={brushSize} onChange={(e) => setBrushSize(Number(e.target.value))} />
            <span className="menu-panel-value">{brushSize}</span>
          </div>
          <div className="menu-panel-section">
            <span className="menu-panel-label">{t('Glitter intensity', 'עוצמת נצנצים')}</span>
            <input type="range" min="0.1" max="1" step="0.1" value={glitterIntensity} onChange={(e) => setGlitterIntensity(Number(e.target.value))} />
            <span className="menu-panel-value">{Math.round(glitterIntensity * 100)}%</span>
          </div>
          <div className="menu-panel-section">
            <span className="menu-panel-label">{t('Letters / numbers', 'אותיות / מספרים')}</span>
            <select onChange={(e) => handleSelectStampValue(e.target.value)} defaultValue="">
              <option value="">א–ת</option>
              {HEBREW_LETTERS.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
            <select onChange={(e) => handleSelectStampValue(e.target.value)} defaultValue="">
              <option value="">A–Z</option>
              {ENGLISH_LETTERS.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
            <select onChange={(e) => handleSelectStampValue(e.target.value)} defaultValue="">
              <option value="">1–10</option>
              {NUMBERS.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </div>
        </section>
      )}

      <section className="menu-panel-card menu-panel-special-box" role="group" aria-label={t('Special fonts and symbols', 'פונטים וסמלים מיוחדים')}>
        <h3 className="menu-panel-card-title menu-panel-special-head">{t('Special fonts & symbols', 'פונטים וסמלים מיוחדים')}</h3>
        <label className="menu-panel-special-font">
          <span>{t('Stamp font', 'פונט להחתמה')}</span>
          <select value={stampFontFamily || "'Heebo', -apple-system, system-ui, sans-serif"} onChange={(e) => onSelectStampFont?.(e.target.value)}>
            <option value="'Heebo', -apple-system, system-ui, sans-serif">Heebo</option>
            <option value="'Fredoka', 'Varela Round', 'Heebo', sans-serif">Fredoka</option>
            <option value="'Varela Round', 'Heebo', sans-serif">Varela Round</option>
            <option value="'Times New Roman', serif">Times</option>
            <option value="'Courier New', monospace">Courier</option>
          </select>
        </label>
        <div className="menu-panel-symbol-grid">
          {SPECIAL_SYMBOLS.map((sym) => (
            <button
              key={sym}
              type="button"
              className="menu-panel-symbol-btn"
              onClick={() => {
                handleSelectStampValue(sym)
              }}
              title={t(`Use symbol ${sym}`, `השתמש בסמל ${sym}`)}
            >
              {sym}
            </button>
          ))}
        </div>
      </section>

      {children}

      <section className="menu-panel-card" aria-label={t('Quick actions', 'פעולות מהירות')}>
        <h3 className="menu-panel-card-title">{t('Quick actions', 'פעולות מהירות')}</h3>
        <div className="menu-panel-actions menu-panel-actions--compact">
          {actionButtonsTop.map((btn) => (
            <button key={btn.key} type="button" onClick={btn.onClick} disabled={btn.disabled} title={btn.title || btn.label}>
              {btn.label}
            </button>
          ))}
        </div>
      </section>

      <section className="menu-panel-card" aria-label={t('Image actions', 'פעולות תמונה')}>
        <h3 className="menu-panel-card-title">{t('Image actions', 'פעולות תמונה')}</h3>
        <div className="menu-panel-actions">
          {actionButtonsMiddle.map((btn) => (
            <button key={btn.key} type="button" onClick={btn.onClick} disabled={btn.disabled} title={btn.title || btn.label}>
              {btn.label}
            </button>
          ))}
        </div>
      </section>

      {actionButtonsBottom.length > 0 && (
        <section className="menu-panel-card" aria-label={t('Export and sharing', 'ייצוא ושיתוף')}>
          <h3 className="menu-panel-card-title">{t('Export and sharing', 'ייצוא ושיתוף')}</h3>
          <div className="menu-panel-actions">
            {actionButtonsBottom.map((btn) => (
              <button key={btn.key} type="button" className={btn.className || ''} onClick={btn.onClick} disabled={btn.disabled} title={btn.title || btn.label}>
                {btn.label}
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
